import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Typography,
  Chip,
  TablePagination,
  CircularProgress,
  Snackbar,
  Alert,
  Grid,
  Link,
  Tooltip,
} from '@mui/material';
import {
  Search,
  FilterAltOff,
  Visibility,
  Close,
  Business,
  Language,
  Email,
  Phone,
} from '@mui/icons-material';
import { providerApplicationsAPI } from '../../services/api';
import { clearFilterButtonSx } from '../../utils/clearFilterButtonSx';
import { useLanguage } from '../../context/LanguageContext';

const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const dateObj = new Date(dateStr);
    if (isNaN(dateObj.getTime())) return dateStr;
    return dateObj.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
};

const ProviderApplications = () => {
  const { t } = useLanguage();

  const [isLoading, setIsLoading] = useState(false);
  const [applications, setApplications] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const [page, setPage] = useState(0); // MUI 0-based
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Detail Modal State
  const [selectedApp, setSelectedApp] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  // Toast Notification
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

  const debouncedSearchRef = useRef(debouncedSearch);

  useEffect(() => {
    debouncedSearchRef.current = debouncedSearch;
  }, [debouncedSearch]);

  // Debounced search logic
  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = (searchQuery || '').trim();
      if (debouncedSearchRef.current !== next) {
        setPage(0);
      }
      setDebouncedSearch(next);
    }, 400);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  const showToast = (message, severity = 'info') => {
    setToast({ open: true, message, severity });
  };

  const handleCloseToast = () => {
    setToast((prev) => ({ ...prev, open: false }));
  };

  const fetchApplications = useCallback(async () => {
    setIsLoading(true);
    try {
      // API expects 1-based page index
      const apiPage = page + 1;
      const response = await providerApplicationsAPI.getProviderApplications(
        apiPage,
        rowsPerPage,
        debouncedSearch
      );

      if (response?.data?.success === true && Array.isArray(response.data.payload)) {
        setApplications(response.data.payload);
        const total = response.data.totalRecord ?? response.data.totalCount ?? response.data.payload.length;
        setTotalCount(Number(total) >= 0 ? Number(total) : response.data.payload.length);
      } else {
        setApplications([]);
        setTotalCount(0);
        const errMsg = response?.data?.errorMessage || 'Failed to fetch provider applications';
        showToast(errMsg, 'error');
      }
    } catch (error) {
      console.error('Error loading provider applications:', error);
      setApplications([]);
      setTotalCount(0);
      const errMsg = error?.response?.data?.errorMessage || error?.message || 'Error connecting to server';
      showToast(errMsg, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDetail = (app) => {
    setSelectedApp(app);
    setDetailDialogOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailDialogOpen(false);
    setSelectedApp(null);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setDebouncedSearch('');
    setPage(0);
  };

  return (
    <Box p={3}>
      {/* Screen Title & Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        mb={3}
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={1}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#0F3D3E' }}>
            {t('providerApplications.title') || 'Provider Applications'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('providerApplications.subtitle') ||
              'Review and manage provider applications submitted by business owners.'}
          </Typography>
        </Box>
        <Chip
          label={`${totalCount} ${totalCount === 1 ? 'Application' : 'Applications'}`}
          sx={{
            bgcolor: '#E6F4F1',
            color: '#0F3D3E',
            fontWeight: 600,
            fontSize: '0.9rem',
            px: 1,
            py: 2.2,
            borderRadius: '12px',
          }}
        />
      </Box>

      {/* Search & Filter Bar */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: 2.5,
          bgcolor: '#ffffff',
          borderRadius: 3,
          border: '1px solid #E5E7EB',
          boxShadow: '0 2px 6px rgba(0,0,0,0.03)',
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={8} md={6}>
            <TextField
              size="small"
              fullWidth
              label={t('common.search') || 'Search'}
              placeholder={
                t('providerApplications.searchPh') ||
                'Search by name, business, category, email, phone...'
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ color: '#0F3D3E', mr: 1, fontSize: 20 }} />,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  '&:hover fieldset': { borderColor: '#0F3D3E' },
                  '&.Mui-focused fieldset': { borderColor: '#0F3D3E' },
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md="auto" sx={{ ml: { md: 'auto' } }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterAltOff />}
              onClick={handleClearFilters}
              sx={clearFilterButtonSx}
            >
              {t('common.clearFilters') || 'Clear Filters'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Main Table Container */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: '1px solid #E5E7EB',
          overflow: 'hidden',
          boxShadow: '0 2px 6px rgba(0,0,0,0.03)',
        }}
      >
        <TableContainer>
          <Table sx={{ minWidth: 900 }}>
            <TableHead sx={{ bgcolor: '#F9FAFB' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: '#374151' }}>
                  {t('providerApplications.colBusinessName') || 'Business Name'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#374151' }}>
                  {t('providerApplications.colApplicant') || 'Applicant'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#374151' }}>
                  {t('providerApplications.colCategory') || 'Category'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#374151' }}>
                  {t('providerApplications.colContact') || 'Contact Info'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#374151' }}>
                  {t('providerApplications.colIntroCall') || 'Intro Call'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#374151' }}>
                  {t('providerApplications.colSubmitted') || 'Submitted Date'}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: '#374151' }}>
                  {t('providerApplications.colActions') || 'Actions'}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={40} sx={{ color: '#0F3D3E' }} />
                    <Typography variant="body2" sx={{ mt: 1.5, color: 'text.secondary' }}>
                      Loading provider applications...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : applications.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <Business sx={{ fontSize: 48, color: '#9CA3AF', mb: 1 }} />
                    <Typography variant="h6" sx={{ color: '#4B5563', fontWeight: 600 }}>
                      {t('providerApplications.emptyTitle') || 'No Provider Applications Found'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5 }}>
                      {debouncedSearch
                        ? 'Try adjusting your search criteria.'
                        : 'No provider applications have been submitted yet.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                applications.map((app) => (
                  <TableRow
                    key={app.applicationId || app.userId}
                    hover
                    sx={{
                      '&:hover': { bgcolor: '#F9FAFB' },
                      transition: 'background-color 0.2s',
                    }}
                  >
                    {/* Business Name */}
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                          {app.businessName || '-'}
                        </Typography>
                        {app.website && (
                          <Link
                            href={
                              app.website.startsWith('http') ? app.website : `https://${app.website}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            variant="caption"
                            sx={{ color: '#0284C7', display: 'inline-flex', alignItems: 'center', gap: 0.3 }}
                          >
                            <Language sx={{ fontSize: 13 }} />
                            {app.website.replace(/^https?:\/\//, '')}
                          </Link>
                        )}
                      </Box>
                    </TableCell>

                    {/* Applicant & Role */}
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#111827' }}>
                          {app.name || '-'}
                        </Typography>
                        {app.role && (
                          <Chip
                            label={app.role}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.72rem',
                              fontWeight: 500,
                              bgcolor: '#F3F4F6',
                              color: '#374151',
                              mt: 0.3,
                            }}
                          />
                        )}
                      </Box>
                    </TableCell>

                    {/* Category */}
                    <TableCell>
                      {app.category ? (
                        <Chip
                          label={app.category}
                          size="small"
                          sx={{
                            bgcolor: '#E0F2FE',
                            color: '#0369A1',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                          }}
                        />
                      ) : (
                        '-'
                      )}
                    </TableCell>

                    {/* Contact Info */}
                    <TableCell>
                      <Box display="flex" flexDirection="column" gap={0.3}>
                        {app.email && (
                          <Typography
                            variant="caption"
                            sx={{ color: '#4B5563', display: 'flex', alignItems: 'center', gap: 0.5 }}
                          >
                            <Email sx={{ fontSize: 13, color: '#6B7280' }} />
                            {app.email}
                          </Typography>
                        )}
                        {app.phone && (
                          <Typography
                            variant="caption"
                            sx={{ color: '#4B5563', display: 'flex', alignItems: 'center', gap: 0.5 }}
                          >
                            <Phone sx={{ fontSize: 13, color: '#6B7280' }} />
                            {app.phone}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>

                    {/* Intro Call */}
                    <TableCell>
                      <Typography variant="body2" sx={{ color: '#374151', fontSize: '0.85rem' }}>
                        {app.introCall || '-'}
                      </Typography>
                    </TableCell>

                    {/* Submitted Date */}
                    <TableCell>
                      <Typography variant="caption" sx={{ color: '#6B7280', whiteSpace: 'nowrap' }}>
                        {formatDateTime(app.createdAtUtc)}
                      </Typography>
                    </TableCell>

                    {/* Actions */}
                    <TableCell align="center">
                      <Tooltip title={t('common.viewDetails') || 'View Details'}>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDetail(app)}
                          sx={{
                            color: '#0F3D3E',
                            bgcolor: '#E6F4F1',
                            '&:hover': { bgcolor: '#cde9e5' },
                          }}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 20, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ borderTop: '1px solid #E5E7EB' }}
        />
      </Paper>

      {/* Application Details Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={handleCloseDetail}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
          },
        }}
      >
        {selectedApp && (
          <>
            <DialogTitle
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                pb: 1,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827' }}>
                Application Details
              </Typography>
              <IconButton onClick={handleCloseDetail} size="small">
                <Close />
              </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ py: 2.5 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Name
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                    {selectedApp.name || '-'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Business Name
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                    {selectedApp.businessName || '-'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Role
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#374151' }}>
                    {selectedApp.role || '-'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Category
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#374151' }}>
                    {selectedApp.category || '-'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Email
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#374151' }}>
                    {selectedApp.email || '-'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Phone
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#374151' }}>
                    {selectedApp.phone || '-'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Website
                  </Typography>
                  {selectedApp.website ? (
                    <Link
                      href={
                        selectedApp.website.startsWith('http')
                          ? selectedApp.website
                          : `https://${selectedApp.website}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                      underline="hover"
                      sx={{ color: '#0F3D3E', fontWeight: 500 }}
                    >
                      {selectedApp.website}
                    </Link>
                  ) : (
                    <Typography variant="body2" sx={{ color: '#374151' }}>-</Typography>
                  )}
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Address
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#374151' }}>
                    {selectedApp.address || '-'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Intro Call
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#374151' }}>
                    {selectedApp.introCall || '-'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Time Window
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#374151' }}>
                    {selectedApp.timeWindow || '-'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Info Consent
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#374151' }}>
                    {selectedApp.infoConsent ? 'Yes' : 'No'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Submitted Date
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#374151' }}>
                    {formatDateTime(selectedApp.createdAtUtc)}
                  </Typography>
                </Grid>

                {selectedApp.description && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Description
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#374151', mt: 0.3, whiteSpace: 'pre-wrap' }}>
                      {selectedApp.description}
                    </Typography>
                  </Grid>
                )}

                {selectedApp.info && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Additional Info
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#374151', mt: 0.3, whiteSpace: 'pre-wrap' }}>
                      {selectedApp.info}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 1.5 }}>
              <Button
                onClick={handleCloseDetail}
                variant="contained"
                size="small"
                sx={{
                  bgcolor: '#0F3D3E',
                  '&:hover': { bgcolor: '#0a2829' },
                  borderRadius: '8px',
                  px: 3,
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Snackbar Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseToast} severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProviderApplications;
